import React from "react";

const BadBossesVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson reframes the bad boss problem. Most managers do not change, so growth comes from learning their language. It maps common types: the logical cold boss, the anxious micromanager, the high standards critic, the absent ghost, and the image hungry narcissist. Instead of venting, it offers workarounds like speaking in deliverables, giving structured control, staying calm under pressure, making wins easy to notice, and building third party praise.
      </p>
    </div>
  );

  const transcriptHtml = `
    <p>How many times have you heard someone saying, "<strong>My boss is terrible.</strong>"</p>

    <p>It's not surprising because studies show that <strong>75% of workers</strong> rank "dealing with their boss" as the most stressful part of their job, with long-term effects such as anxiety, depression, and disengagement.</p>

    <p>But let's pause for a second. There are no bad bosses, unless you're working for true evil, which, let's be honest, is pretty rare.</p>

    <p>There are just bosses who are <strong>good for you</strong>, and bosses who are <strong>not so good for you.</strong></p>

    <p>Your boss won't change. Why would they? The very behavior that annoys you probably got them promoted.</p>

    <p>So stop waiting for a personality transplant. Complaining about your boss is corporate self-torture. It changes nothing for them, but drains everything from you.</p>

    <p>Every minute you spend diagnosing your boss's personality is a minute not spent navigating it. They're not losing sleep over your frustration. You're the one replaying conversations, rewriting emails, and carrying that emotional weight into your next meeting.</p>

    <p><strong>Your job is to find a workaround and win in spite of them.</strong></p>

    <p>Let's meet the usual suspects.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight:700; font-size:1.125rem;">1. The Emotionally Distant Boss</h3>
    <p>Some bosses just don't get emotions. They're logical, efficient, and cold. You could be on fire, and they'd still ask, "Did you finish the report?"</p>
    <p>If that's your boss, don't take it personally. They simply lack the empathy chip.</p>
    <p><strong>Best way to manage them?</strong></p>
    <ul style="margin-left:20px;">
      <li>Don't expect emotional validation. You don't have a bad boss problem, you have an expectation problem.</li>
      <li>Be transactional. Think of them like a vending machine: insert data, get output. No feelings involved.</li>
      <li>They may not comfort you, but they're great allies when you need to push something up the chain. With them, speak in deliverables, not feelings.</li>
    </ul>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight:700; font-size:1.125rem;">2. The Micromanager</h3>
    <p>Classic right? We've all had one such manager in our lives, maybe you still have?</p>
    <p>They hover, they question, they "just check in" and you can't even breathe without a "quick sync."</p>
    <p>Micromanagers are not evil, they're anxious. They fear losing control.</p>
    <p><strong>So what's the trick?</strong> Give them control of course, but in your own way.</p>
    <ul style="margin-left:20px;">
      <li>Stay with them long enough to learn their preferences.</li>
      <li>The more they depend on you to execute their "way," the more irreplaceable you become.</li>
      <li>Once you hold the knowledge base of how they operate, <strong>loss aversion</strong> kicks in. They'll fight to keep you.</li>
    </ul>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight:700; font-size:1.125rem;">3. The "Aggressive Uncle"</h3>
    <p>Always dissatisfied. Always yelling. Sometimes they even insult you, not your work.</p>
    <p>They are all about standards. They test you to see if you can survive pressure, because that's how they were built.</p>
    <p><strong>The trick?</strong></p>
    <ul style="margin-left:20px;">
      <li>Don't flinch. Don't fight back. Prove consistency.</li>
      <li>Their anger is a storm; if you stay calm through it, you earn rare respect.</li>
      <li>They don't reward emotions, they reward endurance.</li>
    </ul>
    <p>It's easy to hate them, but here's the twist: <strong>These people often rise the fastest.</strong></p>
    <p>Survive them once, and you'll have them in your corner for life.</p>
    <p>For them, you'll have to:</p>
    <ul style="margin-left:20px;">
      <li>Develop a thick skin. Don't react emotionally; they're testing your composure under fire.</li>
      <li>Set boundaries quietly. Define your red lines with calm firmness, they respect that.</li>
      <li>Ask for guidance. It disarms them completely.</li>
    </ul>
    <p>Over time, these same critics often turn into your loudest supporters, because you passed their stress test and now your heart is ready for anything.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight:700; font-size:1.125rem;">4. The Ghost Boss</h3>
    <p>Now here's a tricky one—the "Ghost Boss." Barely around, gives no feedback, and vanishes when you need them.</p>
    <p>You might think, "This is great, freedom!" But come appraisal time, you realize they have no idea what you actually did.</p>
    <p><strong>Fix it early.</strong></p>
    <ul style="margin-left:20px;">
      <li>Make your wins visible, get peers or clients to copy them on appreciation emails.</li>
      <li>Block time with them proactively.</li>
      <li>Subtly remind them of your goals and progress.</li>
    </ul>
    <p>Don't expect them to notice you, <strong>make noticing you effortless.</strong></p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight:700; font-size:1.125rem;">5. The Narcissist Boss</h3>
    <p>These are the toughest category, self-absorbed, image-obsessed bosses. They crave admiration, control, and being the hero in every story (especially yours).</p>
    <p>Their world has one rule: <strong>they are the main character.</strong></p>
    <p>You can't outshine them, but you can outsmart them.</p>
    <p><strong>Here's how:</strong></p>
    <ul style="margin-left:20px;">
      <li>Detach emotionally. Their manipulation thrives on reaction, so stop feeding the drama.</li>
      <li>Link your success to their ego. Frame your wins as their wins. Say things like, "Your strategy helped me close this deal." It soothes their need for credit.</li>
      <li>Build third-party advocacy. Get others, clients, peers, leaders, to validate your work. Narcissists trust outside praise more than their own team's.</li>
    </ul>
    <p>And here's the irony: when you make your boss look good, they start depending on you to keep their image alive.</p>

    <p>Terrible bosses create toxic workplaces. They cause anxiety, burnout, even depression.</p>
    <p>But not all bosses are monsters. Some just have flaws, the same way we all do.</p>

    <p><strong>The game isn't to fix them, it's to navigate them.</strong> It doesn't make promotions impossible, just more tedious.</p>

    <p>Think about your boss. Which type are they closest to?</p>
    <p>And what if… instead of fighting that type, you learned their language?</p>

    <p><strong>The fastest way to grow is not by finding the perfect manager, it's by learning how to lead yourself under imperfect ones.</strong></p>

    <p><strong>Which of these boss levels are you going to beat?</strong></p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 overflow-y-scroll font-gotham text-body leading-7 text-[#484848] max-h-[400px]"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default BadBossesVideo;
